import { Button, NotificationDispatch, StyledFlex, Tooltip } from '@tryhackme/thm-ui-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay } from '@fortawesome/pro-solid-svg-icons';
import { faArrowRotateRight, faPlus } from '@fortawesome/free-solid-svg-icons';
import { addHours, format } from 'date-fns';
import { useNetworkInstance, userNetworkInstanceSocketConfig } from 'src/common/hooks/use-network-instance';
import { NetworkInstanceStatus } from 'src/common/interfaces';
import { RoomTask } from 'src/common/interfaces/tasks';
import { useGetUserQuery } from 'src/common/slices';
import { useDeployNetworkInstanceMutation, useGetCurrentActiveNetworkQuery } from 'src/common/slices/networks-instances';
import { StyledControls, StyledNetworks, StyledNetworkTime, StyledTooltip, StyledWrapperButton } from 'src/features/room/components/networks/networks.styles';
import { ExtendTimeLeft } from 'src/features/room/components/networks/components/extend-time-left';
import { useGetNetworkTemplateQuery } from 'src/features/networks/networks.slice';
import { DiagramDisplay } from 'src/features/networks/components/diagram-display';
const {
  DEPLOYING,
  RUNNING,
  DELETING,
  DELETED,
  PAUSED
} = NetworkInstanceStatus;
export const Networks = ({
  templateId,
  roomId,
  tasks
}: {
  templateId: string;
  roomId: string;
  tasks: RoomTask[];
}) => {
  const {
    data: userData
  } = useGetUserQuery();
  const {
    data: networkTemplate
  } = useGetNetworkTemplateQuery({
    templateId
  });
  const [requestNetworkDeploy, {
    isLoading: isRequestingDeploy
  }] = useDeployNetworkInstanceMutation();
  const {
    data: restNetworkInstance,
    isLoading: fetchingNetworkInstance
  } = useGetCurrentActiveNetworkQuery({
    templateId,
    userId: userData?.data?.user._id
  });
  const {
    extendNetworkPauseTime,
    handleResetRequest,
    isNetworkUpdating,
    resetsRequested,
    resetsRequired,
    timeSinceStart,
    status,
    startTime,
    pauseTime,
    networkDiagramNodes,
    networkDiagramEdges,
    timeUntilNetworkPause
  } = useNetworkInstance({
    restNetworkInstance,
    tasks,
    eventSubscription: userNetworkInstanceSocketConfig
  });
  const handleStart = () => {
    if (status === PAUSED) {
      extendNetworkPauseTime(addHours(new Date(), 2));
    } else {
      requestNetworkDeploy({
        templateId,
        roomId
      }).catch(() => NotificationDispatch('error', 'Oops'));
    }
  };
  return <StyledNetworks data-testid="networks" data-sentry-element="StyledNetworks" data-sentry-component="Networks" data-sentry-source-file="networks.tsx">
      {fetchingNetworkInstance ? <>Loading</> : <>
          {networkDiagramNodes && networkDiagramEdges ? <DiagramDisplay nodes={networkDiagramNodes} edges={networkDiagramEdges} status={status} isRequestingDeploy={isRequestingDeploy} /> : networkTemplate && <DiagramDisplay nodes={networkTemplate?.networkDiagram.nodes} edges={networkTemplate?.networkDiagram.connections} />}

          <StyledControls>
            <StyledFlex gap="1.6rem" justifyContent="flex-start">
              <StyledWrapperButton>
                <Button onClick={handleStart} disabled={status && [RUNNING, DEPLOYING, DELETING, DELETED].includes(status)} isLoading={isRequestingDeploy}>
                  {' '}
                  <FontAwesomeIcon icon={faPlay} />
                  Start
                </Button>
                <StyledTooltip data-wrapper="tooltip" data-tooltip="first">
                  <Tooltip>The network will go to sleep if there is no activity detected</Tooltip>
                </StyledTooltip>
              </StyledWrapperButton>
              {pauseTime && <StyledWrapperButton>
                  <Button color="hint" data-testid="network-diagram-extend" isLoading={false} isDisabled={!status || [PAUSED, DEPLOYING, DELETING, DELETED].includes(status)} onClick={() => extendNetworkPauseTime(addHours(new Date(pauseTime), 1))}>
                    <FontAwesomeIcon icon={faPlus} /> Extend{' '}
                    {timeUntilNetworkPause ? <ExtendTimeLeft pauseTime={timeUntilNetworkPause} /> : ''}
                  </Button>
                  <StyledTooltip data-wrapper="tooltip">
                    <Tooltip>The network will go to sleep if there is no activity</Tooltip>
                  </StyledTooltip>
                </StyledWrapperButton>}
              <StyledWrapperButton>
                <Button isLoading={isNetworkUpdating} color="error" onClick={handleResetRequest} data-testid="network-diagram-reset" disabled={status !== RUNNING || resetsRequested === resetsRequired}>
                  <FontAwesomeIcon icon={faArrowRotateRight} /> {`Reset ${resetsRequested ?? 0}/${resetsRequired ?? 0}`}
                </Button>
                <StyledTooltip data-wrapper="tooltip">
                  <Tooltip>When there are enough votes, the network will reset to its default state</Tooltip>
                </StyledTooltip>
              </StyledWrapperButton>
            </StyledFlex>
            {status === RUNNING && startTime && <StyledNetworkTime data-testid="network-uptime">Network up time: {timeSinceStart}</StyledNetworkTime>}
            {status === PAUSED && pauseTime && <StyledNetworkTime data-testid="network-uptime">
                Network paused since: {format(new Date(pauseTime), `MMM do' at 'HH:mm`)}
              </StyledNetworkTime>}
          </StyledControls>
        </>}
    </StyledNetworks>;
};